// skipcq: JS-W1028, JS-0128
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import { parseSheetCsv } from '../../../utils/sheetBeerImport/parseSheetCsv';
import {
  validateSheetRows,
  getBreweryGroups,
  breweryGroupKey,
} from '../../../utils/sheetBeerImport/validateSheetRows';
import useBeerStyles from '../../../hooks/useBeerStyles';
import useBeerStyleTypes from '../../../hooks/useBeerStyleTypes';
import { buildCatalogStyleMapping } from '../../../utils/beerStyle/catalogStyleMapping';
import { useSheetBeerImport } from '../../../hooks/useSheetBeerImport';
import BreweryMatchingStep from './BreweryMatchingStep';
import SheetBeerReviewTable from './SheetBeerReviewTable';
import ImportSummaryView from '../../brewery/findBeer/ImportSummaryView';

const STEPS = {
  UPLOAD: 0,
  BREWERY_MATCH: 1,
  REVIEW: 2,
  SUMMARY: 3,
};

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

/**
 * ImportBeerDialog
 * Multi-step wizard for importing beers from a CSV export of a Google Sheet.
 */
function ImportBeerDialog({ open, onClose, onImportSuccess }) {
  const [step, setStep] = useState(STEPS.UPLOAD);
  const [filename, setFilename] = useState('');
  const [parseError, setParseError] = useState(null);
  const [validatedRows, setValidatedRows] = useState([]);
  const [breweryGroups, setBreweryGroups] = useState([]);
  const [brewerySelections, setBrewerySelections] = useState({});
  const [selectedRowIndices, setSelectedRowIndices] = useState([]);
  const [importResults, setImportResults] = useState(null);

  const { refresh: refreshBeerStyles } = useBeerStyles();
  const { refresh: refreshStyleTypes } = useBeerStyleTypes();
  const {
    importSheetBeers,
    loading: importLoading,
    error: importError,
    progress,
    resetProgress,
  } = useSheetBeerImport();

  const openRef = useRef(open);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const resetDialog = useCallback(() => {
    setStep(STEPS.UPLOAD);
    setFilename('');
    setParseError(null);
    setValidatedRows([]);
    setBreweryGroups([]);
    setBrewerySelections({});
    setSelectedRowIndices([]);
    setImportResults(null);
    resetProgress();
  }, [resetProgress]);

  useEffect(() => {
    if (!open) {
      resetDialog();
    }
  }, [open, resetDialog]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const runValidation = useCallback(
    async (rows) => {
      const latestStyles = await refreshBeerStyles();
      const latestTypes = await refreshStyleTypes();
      if (!latestStyles || !latestTypes) {
        if (openRef.current) {
          setParseError('Failed to load beer style catalog. Try again.');
        }
        return [];
      }
      const styleIdSet = new Set(latestStyles.map((style) => style.id));
      const catalogMapping = buildCatalogStyleMapping(latestStyles);
      const allowedTypeNames = latestTypes.map((typeRow) => typeRow.name).filter(Boolean);
      const validated = validateSheetRows(rows, catalogMapping, styleIdSet, allowedTypeNames);
      if (!openRef.current) {
        return validated;
      }
      setValidatedRows(validated);
      setBreweryGroups(getBreweryGroups(validated));
      setBrewerySelections({});
      setSelectedRowIndices(
        validated.filter((row) => row.isValid).map((row) => row.rowIndex),
      );
      return validated;
    },
    [refreshBeerStyles, refreshStyleTypes],
  );

  const handleFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      setParseError(null);
      setFilename(file.name);

      try {
        const text = await readFileAsText(file);
        const { rows, headerError, parseError: csvError } = parseSheetCsv(text);
        if (headerError || csvError) {
          if (openRef.current) {
            setParseError(headerError || csvError);
          }
          return;
        }
        const validated = await runValidation(rows);
        if (openRef.current && validated.length > 0) {
          setStep(STEPS.BREWERY_MATCH);
        }
      } catch (readError) {
        if (openRef.current) {
          setParseError(readError?.message || 'Failed to read CSV file');
        }
      } finally {
        event.target.value = '';
      }
    },
    [runValidation],
  );

  const handleSelectBrewery = useCallback((groupKey, breweryId, label) => {
    setBrewerySelections((prev) => ({
      ...prev,
      [groupKey]: { breweryId, label },
    }));
  }, []);

  const breweryIdByGroupKey = useMemo(() => {
    const map = {};
    Object.entries(brewerySelections).forEach(([key, value]) => {
      if (value?.breweryId) {
        map[key] = value.breweryId;
      }
    });
    return map;
  }, [brewerySelections]);

  const matchableGroups = breweryGroups.filter((group) => !group.hasStateError);
  const allGroupsMatched = matchableGroups.every(
    (group) => breweryIdByGroupKey[group.key],
  );

  const selectedValidRows = useMemo(
    () =>
      validatedRows.filter(
        (row) => row.isValid && selectedRowIndices.includes(row.rowIndex),
      ),
    [validatedRows, selectedRowIndices],
  );

  const selectedRowsMissingBrewery = selectedValidRows.some((row) => {
    const key = breweryGroupKey(row.breweryName, row.stateCode);
    return !breweryIdByGroupKey[key];
  });

  const handleContinueToReview = useCallback(() => {
    setStep(STEPS.REVIEW);
  }, []);

  const handleImport = useCallback(async () => {
    const result = await importSheetBeers({
      validatedRows,
      breweryIdByGroupKey,
      selectedRowIndices,
      sourceFilename: filename,
    });
    if (result) {
      setImportResults(result);
      setStep(STEPS.SUMMARY);
      if (onImportSuccess) {
        onImportSuccess(result);
      }
    }
  }, [
    breweryIdByGroupKey,
    filename,
    importSheetBeers,
    onImportSuccess,
    selectedRowIndices,
    validatedRows,
  ]);

  const progressPercent =
    progress.total > 0 ? (progress.completed / progress.total) * 100 : 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import Beer from CSV</DialogTitle>
      <DialogContent dividers>
        {step === STEPS.UPLOAD && (
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Upload a CSV exported from Google Sheets with columns:
              breweryName, state, beerName, styleLabel, abv, styleType.
            </Typography>
            <Button variant="outlined" component="label">
              Choose CSV file
              <input
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={handleFileChange}
              />
            </Button>
            {parseError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {parseError}
              </Alert>
            )}
          </Box>
        )}

        {step === STEPS.BREWERY_MATCH && (
          <BreweryMatchingStep
            groups={breweryGroups}
            brewerySelections={brewerySelections}
            onSelectBrewery={handleSelectBrewery}
          />
        )}

        {step === STEPS.REVIEW && (
          <SheetBeerReviewTable
            validatedRows={validatedRows}
            selectedIndices={selectedRowIndices}
            onSelectionChange={setSelectedRowIndices}
          />
        )}

        {step === STEPS.SUMMARY && importResults && (
          <ImportSummaryView importResults={importResults} />
        )}

        {importLoading && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Importing batch {progress.completed} of {progress.total}…
            </Typography>
            <LinearProgress variant="determinate" value={progressPercent} />
          </Box>
        )}

        {importError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {importError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        {step === STEPS.BREWERY_MATCH && (
          <Button
            variant="contained"
            onClick={handleContinueToReview}
            disabled={!allGroupsMatched}
          >
            Continue to review
          </Button>
        )}
        {step === STEPS.REVIEW && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={
              importLoading
              || selectedRowIndices.length === 0
              || selectedRowsMissingBrewery
            }
          >
            Import selected beers
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

ImportBeerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onImportSuccess: PropTypes.func,
};

ImportBeerDialog.defaultProps = {
  onImportSuccess: null,
};

export default ImportBeerDialog;
